import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { SortableTagList } from '@/components/admin/corkBoard/desktop/SortableTagList';
import { TagStatsHeader } from '@/components/admin/corkBoard/TagStatsHeader';
import { DesktopTagPanel } from '@/components/admin/corkBoard/desktop/DesktopTagPanel';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { useTagManagement } from '@/hooks/admin/useTagManagement';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface DesktopTagManagementViewProps {
  tagManagement: ReturnType<typeof useTagManagement>;
  formVisible: boolean;
  editingTag: CorkBoardTag | null;
  onOpenCreate: () => void;
  onOpenEdit: (tag: CorkBoardTag) => void;
  onCloseForm: () => void;
  onSave: (data: { name: string; color: string | null; icon: string | null }) => void;
  onDelete: (tag: CorkBoardTag) => void;
}

export const DesktopTagManagementView: React.FC<DesktopTagManagementViewProps> = ({
  tagManagement,
  formVisible,
  editingTag,
  onOpenCreate,
  onOpenEdit,
  onCloseForm,
  onSave,
  onDelete,
}) => {
  const { redesign, semantic } = useAdminColors();

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Cork Board Tags"
        subtitle="Manage the tags residents use to categorize their posts."
        showAddButton
        onAddPress={onOpenCreate}
        addButtonText="Add Tag"
      />

      {tagManagement.error && (
        <View
          style={[
            styles.errorBanner,
            {
              backgroundColor: redesign.accent.redBg,
              borderBottomColor: semantic.status.error,
            },
          ]}
        >
          <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
            Failed to load tags.
          </Text>
          <TouchableOpacity onPress={tagManagement.refetch}>
            <Text
              style={[
                styles.errorRetryText,
                { color: semantic.status.error },
              ]}
            >
              Retry
            </Text>
          </TouchableOpacity>
        </View>
      )}

      <TagStatsHeader tags={tagManagement.tags} />

      <View style={styles.content}>
        <GlassCard padding={0} style={styles.card}>
          <View
            style={[
              styles.cardHeader,
              { borderBottomColor: redesign.card.border },
            ]}
          >
            <Text style={[styles.cardTitle, { color: redesign.text.primary }]}>Tags</Text>
            <Text style={[styles.cardSubtitle, { color: redesign.text.secondary }]}>
              {tagManagement.tags.length} tag{tagManagement.tags.length !== 1 ? 's' : ''}
            </Text>
          </View>

          <SortableTagList
            tags={tagManagement.tags}
            editingTagId={editingTag?.id ?? null}
            onToggleEnabled={tagManagement.handleToggleEnabled}
            onEdit={onOpenEdit}
            onDelete={onDelete}
            onReorder={(next) => tagManagement.handleDragEnd({ data: next })}
          />
        </GlassCard>
      </View>

      <DesktopTagPanel
        visible={formVisible}
        tag={editingTag}
        isSaving={tagManagement.isCreating || tagManagement.isUpdating}
        onSave={onSave}
        onClose={onCloseForm}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  content: {
    flex: 1,
  },
  card: {
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  cardSubtitle: {
    fontSize: 13,
  },
});
